import {
  Button,
  Card,
  Checkbox,
  Container, FormControl,
  MenuItem, Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import EmailIcon from '@mui/icons-material/Email';
import { debounce } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { useTranslation } from 'react-i18next';

import PageHeader from 'components/PageHeader/PageHeader';
import ActionPopup from 'components/Popup/ActionPopup';
import Popup from 'components/Popup/Popup';
import MoreMenu from 'components/table/MoreMenu';
import SearchNotFound from 'components/table/SearchNoFound';
import TableHeader from 'components/table/TableHeader';
import TableToolBar from 'components/table/TableToolbar';
import { getImpactReportTableHead } from 'constans/table-headers';
import SnackbarContext from 'contexts/snackbarContext';
import { handleClick, handleSelectAllClick, sortByColumn, } from 'services/list.service';

import {
  deleteImpactReport,
  deleteImpactReportBulk,
  getImpactReports,
  searchImpactReports,
} from 'services/impactReport.services';

import { ImpactReportType } from 'types/impactReport.type';

const getDownloadUrl = (rawPath: string): string => {
  if (!rawPath) return '';

  const withoutBraces = rawPath.replace(/[{}]/g, '').trim();
  const envBase = process.env.REACT_APP_API_BASE_URL || window.location.origin;
  const base = envBase.replace(/\/api\/?$/, '').replace(/\/$/, '');

  let relativePath = withoutBraces;
  if (/^https?:\/\//i.test(withoutBraces)) {
    try {
      const url = new URL(withoutBraces);
      relativePath = url.pathname + (url.search || '');
    } catch {
      relativePath = withoutBraces;
    }
  }

  relativePath = relativePath.replace(/^\/?api\/?/, '');
  relativePath = relativePath.replace(/^\/+/, '');

  return `${base}/${relativePath}`;
};

type DownloadFormat = 'docx' | 'pdf' | 'both';

export default function impactReportPage() {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [selected, setSelected] = useState<string[]>([]);
  const [orderBy, setOrderBy] = useState('createdAt');
  const [rowsPerPage, setRowsPerPage] = useState(14);
  const { handleOpen } = useContext(SnackbarContext);
  const [impactReportList, setImpactReportList] = useState<ImpactReportType[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [downloadSelectionOpen, setDownloadSelectionOpen] = useState(false);

  useEffect(() => {
    getImpactReportsHandler();
  }, []);

  const getImpactReportsHandler = () => {
    getImpactReports().then((response) => {

      setImpactReportList(response.data);


    });
  };

  const searchFilter = useCallback(
    debounce((filterName) => {
      if (filterName) {
        searchImpactReports(filterName).then((response) => {
          setImpactReportList(response.data);
        });
      } else {
        getImpactReportsHandler();
      }
    }, 300),
    [],
  );

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteImpactReport = (impactReportId: string) => {
    deleteImpactReport(impactReportId)
      .then(() => {
        setImpactReportList([...impactReportList].filter(({ id }) => id !== impactReportId));
        handleSelectAllClick(false, impactReportList);
        setOpenModal(false);
        handleOpen(t('impactReportPage.impactReportDeletedSuccessMessage'), 'success');
      })
      .catch(({ data }) => {
        handleOpen(data.message);
      });
  };

  const handleDeleteImpactReportBulk = (impactReportIds: string[]) => {
    deleteImpactReportBulk(impactReportIds)
      .then(() => {
        setImpactReportList(impactReportList.filter(({ id }) => !impactReportIds.includes(id)));
        handleSelectAllClick(false, impactReportList);
        setOpenModal(false);
        handleOpen(t('usersPage.impactReportsDeletedSuccessMessage'), 'success');
      })
      .catch(({ data }) => {
        handleOpen(data.message);
      });
  };

  const deleteImpactReportAction = () => {
    if (selected.length > 1) {
      handleDeleteImpactReportBulk(selected);
    } else {
      handleDeleteImpactReport(selected[0]);
    }
  };

  const getPathsByFormat = (
    reports: ImpactReportType[],
    format: Exclude<DownloadFormat, 'both'>,
  ) => {
    return reports.flatMap((report) => {
      const paths = format === 'docx' ? report.documentPathDocx : report.documentPathPDF;
      return (paths ?? []).filter(Boolean);
    });
  };

  const triggerDownloads = async (paths: string[]) => {
    const failedPaths: string[] = [];

    await Promise.all(
      paths.map(async (path) => {
        try {
          const response = await fetch(getDownloadUrl(path), {
            credentials: 'include',
          });

          if (!response.ok) {
            throw new Error(`Download failed with status ${response.status}`);
          }

          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');

          link.href = blobUrl;
          link.download = path.split('/').pop() ?? '';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Let the browser start the download before revoking the object URL.
          setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        } catch (error) {
          console.error('Failed to download impact report file:', error);
          failedPaths.push(path);
        }
      }),
    );

    if (failedPaths.length > 0) {
      handleOpen(`${failedPaths.length} file(s) could not be downloaded.`, 'warning');
    }
  };

  const downloadReports = (
    reports: ImpactReportType[],
    format: DownloadFormat,
  ) => {
    const docxPaths =
      format === 'pdf' ? [] : getPathsByFormat(reports, 'docx');
    const pdfPaths = format === 'docx' ? [] : getPathsByFormat(reports, 'pdf');
    const paths = [...docxPaths, ...pdfPaths];

    if (!paths.length) {
      handleOpen(
        format === 'docx'
          ? t('impactReportPage.noDocxToDownloadMessage')
          : format === 'pdf'
            ? t('impactReportPage.noPdfToDownloadMessage')
            : t('impactReportPage.noFilesToDownloadMessage'),
        'warning',
      );
      return;
    }

    void triggerDownloads(paths);
  };

  const downloadReport = (report: ImpactReportType, format: Exclude<DownloadFormat, 'both'>) => {
    downloadReports([report], format);
  };

  const handleBulkDownload = (format: DownloadFormat) => {
    const selectedReports = impactReportList.filter((report) =>
      selected.includes(report.id),
    );

    setDownloadSelectionOpen(false);
    downloadReports(selectedReports, format);
  };

  const openModalHandler = (id?: string) => {
    setOpenModal(true);
    if (id) {
      setSelected([id]);
    }
  };

  const sortedImpactReportList = sortByColumn(
    impactReportList,
    orderBy as keyof ImpactReportType,
    order,
  );

  const impactReportTableHead = useMemo(getImpactReportTableHead, [language]);

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const gmailAccounts = [
    { label: 'Account 0', index: 0 },
    { label: 'Account 1', index: 1 },
    { label: 'Account 2', index: 2 },
    { label: 'Account 3', index: 3 },
  ];

  const buildGmailUrl = (accountIndex: number, rowArg: ImpactReportType) => {
    const to = (rowArg.recipients ?? []).filter(Boolean).join(',');
    const subject =
      rowArg.emailSubject ?? `Impact Report: ${rowArg.organisation?.companyName ?? ''}`;

    const pdfLinks = rowArg.documentPathPDF?.length
      ? '\n\n' + rowArg.documentPathPDF.map((p: string) => `https://prod.compocity.help/${p}`).join('\n')
      : '';

    const body = `${rowArg.emailText ?? ''}${pdfLinks}`;

    const url = new URL(`https://mail.google.com/mail/u/${accountIndex}/`);
    url.searchParams.set('view', 'cm'); // compose
    url.searchParams.set('to', to);
    url.searchParams.set('su', subject);
    url.searchParams.set('body', body);
    return url.toString();
  };

  const openGmailCompose = (accountIndex: number, rowArg: ImpactReportType) => {
    const url = buildGmailUrl(accountIndex, rowArg);
    const w = window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleMainClick = (rowArg: ImpactReportType) => {
    const last = Number(localStorage.getItem('gmail_u_index'));
    const accountIndex = Number.isInteger(last) ? last : (gmailAccounts[0]?.index ?? 0);
    openGmailCompose(accountIndex, rowArg);
  };

  const handleMenuOpen = (e: React.MouseEvent<HTMLButtonElement>) => setMenuAnchor(e.currentTarget);
  const handleMenuClose = () => setMenuAnchor(null);
  const handleSelectAccount = (idx: number, rowArg: ImpactReportType) => {
    localStorage.setItem('gmail_u_index', String(idx));
    handleMenuClose();
    openGmailCompose(idx, rowArg);
  };

  const downloadActions = [
    {
      label: t('formFields.downloadButtonDocxLabel').toString(),
      onClick: () => handleBulkDownload('docx'),
      variant: 'contained' as const,
    },
    {
      label: t('formFields.downloadButtonPDFLabel').toString(),
      onClick: () => handleBulkDownload('pdf'),
      variant: 'contained' as const,
    },
    {
      label: t('impactReportPage.downloadBothButtonLabel').toString(),
      onClick: () => handleBulkDownload('both'),
      variant: 'contained' as const,
    },
    {
      label: t('popupDialog.cancel').toString(),
      onClick: () => setDownloadSelectionOpen(false),
      variant: 'outlined' as const,
    },
  ];

  return (
    <Container>
      <PageHeader title={t('impactReportPage.title')} buttonTitle={t('impactReportPage.newImpactReportbuttonTitle').toString()} hasButton/>

      <Button variant="outlined" sx={{ margin: '0px 5px 20px 5px' }} component={RouterLink} to={'/impactReportTemplates'}>         {t('impactReportPage.templatesButtonTitle')}</Button>
      <Button variant="outlined" sx={{ margin: '0px 5px 20px 5px' }} component={RouterLink} to={'/impactReportSchedulings'}>       {t('impactReportPage.schedulingsButtonTitle')}</Button>
      <Button variant="outlined" sx={{ margin: '0px 5px 20px 5px' }} component={RouterLink} to={'/impactReportDistances'}>         {t('impactReportPage.distancesButtonTitle')}</Button>
      <Button variant="outlined" sx={{ margin: '0px 5px 20px 5px' }} component={RouterLink} to={'/impactReportDistanceOverrides'}> {t('impactReportPage.distanceOverridesButtonTitle')}</Button>
      <Button variant="outlined" sx={{ margin: '0px 5px 20px 5px' }} component={RouterLink} to={'/impactReportModels'}>            {t('impactReportPage.modelsButtonTitle')}</Button>
      <Button variant="outlined" sx={{ margin: '0px 5px 20px 5px' }} component={RouterLink} to={'/impactReportMaintenanceData'}>   {t('impactReportPage.maintenanceDataButtonTitle')}</Button>

      {/* 
      <Button variant="contained" sx={{ margin: '20px' }} startIcon={<AddIcon />}> {t('formFields.submitButtonLabel')}</Button>
      <Button variant="contained" sx={{ marginTop: '20px' }}  onClick={() => console.log('click') }> {t('formFields.submitButtonLabel')}</Button>
      <IconButton color="primary" onClick={() => handleMyClick()}><DeleteForeverIcon /></IconButton>
      <LoadingButton fullWidth size="large" type="submit" variant="contained" loading={false} >{t('login.loginButton')}</LoadingButton>
      */}

      <Card>
        <TableToolBar
          numSelected={selected.length}
          onFilterName={searchFilter}
          deleteHandler={() => openModalHandler()}
          selectedActions={[
            {
              title: t('formFields.downloadButtonLabel').toString(),
              icon: <DownloadIcon />,
              onClick: () => setDownloadSelectionOpen(true),
            },
          ]}
        />

        <TableContainer>
          <Table>
            {
              <TableHeader
                order={order}
                orderBy={orderBy}
                headLabel={impactReportTableHead}
                rowCount={impactReportList.length}
                numSelected={selected.length}
                onRequestSort={handleRequestSort}
                onSelectAllClick={(checked) =>
                  setSelected(handleSelectAllClick(checked, impactReportList))
                }
              />
            }
            <TableBody>
              {sortedImpactReportList
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => {
                  const {
                    id,
                    organisation,
                    createdAt,
                    dateFrom,
                    dateTo,
                    //documentPath,
                    //model,
                    //template
                  } = row;
                  const isItemSelected = selected.indexOf(id) !== -1;

                  return (
                    <TableRow
                      hover
                      key={id}
                      tabIndex={-1}
                      role="checkbox"
                      selected={isItemSelected}
                      aria-checked={isItemSelected}
                    >

                      <TableCell padding="checkbox"><Checkbox checked={isItemSelected}onClick={() => setSelected(handleClick(id, selected))}/></TableCell>
                      <TableCell align="left">{organisation.companyName}</TableCell>
                      <TableCell align="left">{new Date(dateFrom).toLocaleDateString('hu-HU').replaceAll(' ', '') }</TableCell>
                      <TableCell align="left">{new Date(dateTo).toLocaleDateString('hu-HU').replaceAll(' ', '')  }</TableCell>
                      <TableCell align="left">{new Date(createdAt).toLocaleDateString('hu-HU').replaceAll(' ', '') + ' ' +  new Date(createdAt).toLocaleTimeString('hu-HU').replaceAll(' ', '') }</TableCell>

                      <TableCell align="left">

                        {/*<Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            const recipients = row.recipients?.filter(Boolean).join(',') ?? '';
                            const subject = encodeURIComponent(row.emailSubject ?? `Impact Report: ${organisation.companyName}`);

                            const pdfLinks = row.documentPathPDF?.length
                              ? '\n\n' + row.documentPathPDF.map((path) => `https://prod.compocity.help/${path}`).join('\n')
                              : '';

                            const body = encodeURIComponent(`${row.emailText ?? ''}${pdfLinks}`);

                            window.location.href = `mailto:${recipients}?subject=${subject}&body=${body}`;
                          }}
                        >
                          {t('impactReportPage.sendEmailButton')}
                        </Button>*/}

                        <FormControl size="small">
                          <Select
                            displayEmpty
                            value=""
                            onChange={(e) => handleSelectAccount(Number(e.target.value), row)}
                            renderValue={() => 'Send from…'}
                            sx={{
                              minWidth: 180,
                              borderRadius: 2,
                              '& .MuiSelect-select': {
                                py: 1,
                                px: 2,
                              },
                            }}
                          >
                            {gmailAccounts.map((acc) => (
                              <MenuItem key={acc.index} value={acc.index}>
                                <EmailIcon fontSize="small" style={{ marginRight: 8 }} />
                                {acc.label} (u/{acc.index})
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>


                      </TableCell>
                      

                      <TableCell align="right">
                        <MoreMenu
                          onDelete={() => openModalHandler(id)}
                          id={id}
                          customActions={[
                            {
                              label: t('formFields.downloadButtonDocxLabel').toString(),
                              icon: <DescriptionIcon />,
                              onClick: () => downloadReport(row, 'docx'),
                            },
                            {
                              label: t('formFields.downloadButtonPDFLabel').toString(),
                              icon: <DownloadIcon />,
                              onClick: () => downloadReport(row, 'pdf'),
                            },
                          ]}
                        />
                      </TableCell>

                    </TableRow>
                  );
                })}
            </TableBody>
            {!impactReportList.length && (
              <TableBody>
                <TableRow>
                  <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                    <SearchNotFound />
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[14, 28, 42]}
          component="div"
          count={impactReportList.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, page) => setPage(page)}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t('pagination.rowsPerPage')}
        />
      </Card>
      <Popup
        type="delete"
        open={openModal}
        onConfirm={deleteImpactReportAction}
        handleClose={() => setOpenModal(false)}
      />
      <ActionPopup
        open={downloadSelectionOpen}
        title={t('impactReportPage.downloadDialogTitle').toString()}
        description={t('impactReportPage.downloadDialogDescription').toString()}
        actions={downloadActions}
        onClose={() => setDownloadSelectionOpen(false)}
      />
    </Container>
  );
}
